import { useRef } from "react";
import {
  FaCheckCircle,
  FaFileAlt,
  FaIndustry,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaUsers,
} from "react-icons/fa";
import { useTranslate } from "../../hooks/LanguageProvider";
import { uploadOEMLogo } from "../../services/oemProfile/oemProfile";
import { OEMProfile } from "../../types/Profiles/oemProfile.types";
import { calculateOEMCompletion } from "../../utilities/functions";

const DEFAULT_OEM_LOGO = "/asset/images/default-oem.png";
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://admin.aida-dz.xyz";
const API_ORIGIN = BACKEND_URL.replace(/\/api\/?$/i, "").replace(/\/$/, "");

const getLogoUrl = (logo?: string | null) => {
  if (!logo) return DEFAULT_OEM_LOGO;

  if (logo.startsWith("http://") || logo.startsWith("https://")) {
    return logo;
  }

  const normalizedLogoPath = logo.startsWith("/") ? logo : `/${logo}`;
  if (normalizedLogoPath.startsWith("/uploads/")) {
    return `${API_ORIGIN}/api${normalizedLogoPath}`;
  }

  return `${API_ORIGIN}${normalizedLogoPath}`;
};

interface Props {
  profile: OEMProfile;
  userId?: number | string | null;
  onEdit?: () => void;
  onProfileUpdated?: () => Promise<void> | void;
}

export default function ProfileSidebarCard({
  profile,
  userId,
  onEdit,
  onProfileUpdated,
}: Props) {
  const { translate } = useTranslate();

  const completion = calculateOEMCompletion(profile);

  const completionItems = [
    {
      label: translate("company_information"),
      done:
        !!profile.identity.companyName &&
        !!profile.identity.country &&
        !!profile.identity.city &&
        !!profile.identity.industry &&
        !!profile.identity.description,
    },
    {
      label: translate("contact_information"),
      done:
        !!profile.identity.contactEmail &&
        !!profile.identity.contactPhone &&
        !!profile.identity.website,
    },
    {
      label: translate("industrial_capacity_defined"),
      done:
        profile.industrialPresence.plants > 0 &&
        profile.industrialPresence.assemblyLines > 0 &&
        profile.industrialPresence.productionCapacity > 0 &&
        profile.industrialPresence.sopPrograms > 0,
    },
    {
      label: translate("procurement_segments_added"),
      done: profile.procurementSegments.length > 0,
    },
  ];

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadOEMLogo(file, userId);

      if (onProfileUpdated) {
        await onProfileUpdated();
      }

      e.target.value = "";
    } catch (error) {
      console.error("Erreur upload logo:", error);
    }
  };

  const statusBadgeClass =
    completion >= 80
      ? "bg-success-subtle text-success"
      : completion >= 50
        ? "bg-warning-subtle text-warning"
        : "bg-danger-subtle text-danger";

  const progressColor =
    completion >= 80 ? "#22c55e" : completion >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4">
        <div
          style={{
            height: 55,
            background: "linear-gradient(135deg, #f4b400, #ffcc33)",
          }}
        />

        <div className="card-body text-center p-4" style={{ marginTop: -60 }}>
          <div className="mb-3 d-flex justify-content-center">
            <div
              className="position-relative"
              style={{
                width: 110,
                height: 110,
                overflow: "visible",
              }}
            >
              <div
                className="rounded-circle border border-4 border-white shadow-sm bg-white d-flex align-items-center justify-content-center"
                style={{
                  width: "100%",
                  height: "100%",
                  padding: 12,
                }}
              >
                <img
                  src={getLogoUrl(profile.identity.logo)}
                  onClick={handleClick}
                  onError={(e) => {
                    console.error(
                      "Erreur chargement logo OEM:",
                      getLogoUrl(profile.identity.logo),
                    );
                    (e.target as HTMLImageElement).src = DEFAULT_OEM_LOGO;
                  }}
                  alt={profile.identity.companyName || "OEM logo"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    cursor: "pointer",
                    display: "block",
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleClick}
                className="btn btn-warning rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                style={{
                  position: "absolute",
                  right: 2,
                  bottom: 2,
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: "2px solid #fff",
                  zIndex: 3,
                }}
                title={translate("edit_logo")}
              >
                <i className="fas fa-pen" style={{ fontSize: 12 }} />
              </button>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
          </div>

          <h3 className="fw-bold mb-1">{profile.identity.companyName}</h3>

          <p className="text-muted mb-2 d-flex align-items-center justify-content-center gap-2">
            <FaMapMarkerAlt size={13} />
            <span>
              {profile.identity.city}, {profile.identity.country}
            </span>
          </p>

          <p className="text-muted mb-4 d-flex align-items-center justify-content-center gap-2">
            <FaIndustry size={13} />
            <span>{profile.identity.industry}</span>
          </p>

          <div
            className="rounded-pill px-3 py-3 fw-semibold mb-4"
            style={{
              background: "#e8f7ec",
              color: "#15803d",
              fontSize: "0.95rem",
            }}
          >
            <FaCheckCircle className="me-2" />
            {translate("oem_verified")}
          </div>

          <div className="row g-3 mb-4">
            <div className="col-4">
              <div className="border rounded-4 p-3 h-100 bg-white">
                <div className="mb-2 text-warning">
                  <FaFileAlt size={18} />
                </div>
                <h4 className="fw-bold mb-1">{profile.rfqStats.activeRFQ}</h4>
                <small className="text-muted">{translate("active_rfq")}</small>
              </div>
            </div>

            <div className="col-4">
              <div className="border rounded-4 p-3 h-100 bg-white">
                <div className="mb-2 text-primary">
                  <FaUsers size={18} />
                </div>
                <h4 className="fw-bold mb-1">
                  {profile.rfqStats.suppliersMatched}
                </h4>
                <small className="text-muted">{translate("suppliers")}</small>
              </div>
            </div>

            <div className="col-4">
              <div className="border rounded-4 p-3 h-100 bg-white">
                <div className="mb-2 text-success">
                  <FaProjectDiagram size={18} />
                </div>
                <h4 className="fw-bold mb-1">
                  {profile.rfqStats.projectsInLocalization}
                </h4>
                <small className="text-muted">{translate("projects")}</small>
              </div>
            </div>
          </div>

          {onEdit && (
            <button
              type="button"
              className="btn btn-warning rounded-pill px-4"
              onClick={onEdit}
            >
              {translate("edit_profile")}
            </button>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-semibold mb-0">
              {translate("profile_completion")}
            </h5>
            <span className={`badge rounded-pill ${statusBadgeClass}`}>
              {completion}%
            </span>
          </div>

          <div
            className="progress mb-4"
            style={{
              height: 10,
              borderRadius: 999,
              backgroundColor: "#f1f5f9",
            }}
          >
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${completion}%`,
                backgroundColor: progressColor,
                borderRadius: 999,
              }}
              aria-valuenow={completion}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          <ul className="mb-0 ps-0" style={{ listStyle: "none" }}>
            {completionItems.map((item, index) => (
              <li key={index} className="mb-2 d-flex align-items-center gap-2">
                <span
                  style={{
                    color: item.done ? "#16a34a" : "#9ca3af",
                    fontSize: 14,
                  }}
                >
                  ✔
                </span>
                <span
                  style={{
                    textDecoration: item.done ? "none" : "line-through",
                    color: item.done ? "#111827" : "#9ca3af",
                  }}
                >
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
